import * as React from "react";
import { Link } from "react-router-dom";
import { State } from "@app/geography";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { isEqual } from "lodash";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { JSX } from "react";
import GeographyService from "@app/geography/geographyService";
import { ByCountyRecord } from "@app/shared/data-summaries/byCountyRecord";
import { SummaryResultNetworkType, SummaryResultType } from "@app/shared/data-summaries/enumerations";

interface ByCountyProps {
  providersEndpointFunc: (networkType: SummaryResultNetworkType, resultType: SummaryResultType, key: string) => string;
  records: ByCountyRecord[];
}

interface ByCountyWaffleToppings {
  key: string;
  state: string;
  county: string;
  network: JSX.Element;
  prospect: JSX.Element;
}

interface ByCountyState {
  columnConfig: ColumnConfig;
  selectedState?: string;
  states: State[];
  sortedRecords: ByCountyWaffleToppings[];
}

class ByCounty extends React.PureComponent<ByCountyProps, ByCountyState> {
  private readonly geographyService = new GeographyService();
  private enabledStates: string[] = [];

  constructor(props: ByCountyProps) {
    super(props);

    this.state = {
      columnConfig: {},
      sortedRecords: [],
      states: [],
    };
  }

  public async componentDidMount(): Promise<void> {
    const states = await this.geographyService.getStates();

    states.forEach((state) => {
      if (this.props.records.filter((r) => r.stateAbbreviation === state.abbreviation).length > 0) {
        this.enabledStates.push(state.abbreviation);
      }
    });

    const selectedState = this.enabledStates[0];

    this.setState({
      columnConfig: this.getColumnConfig(),
      selectedState,
      sortedRecords: this.buildRecords(this.visibleRecords(selectedState)),
      states,
    });
  }

  public async componentDidUpdate(prevProps: ByCountyProps, prevState: ByCountyState): Promise<void> {
    if (!isEqual(this.props.records, prevProps.records)) {
      this.setState({
        columnConfig: this.getColumnConfig(),
        sortedRecords: this.buildRecords(this.visibleRecords(prevState.selectedState)),
      });
    }
  }

  public render(): JSX.Element {
    return (
      <>
        <QESSelect
          containerClassName="col-3 mt-3 mb-2 row"
          label="State:"
          testId="by-county__state-dropdown"
          value={this.state.selectedState}
          onChange={this.handleChange}
          placeholder="Loading..."
        >
          {this.state.states.map((state) => (
            <option key={state.abbreviation} value={state.abbreviation} disabled={!this.enabledStates.includes(state.abbreviation)}>{state.name}</option>
          ))}
        </QESSelect>

        <div className="form-group">
          {this.state.states && this.state.sortedRecords.length === 0 ? null : (
            <Waffles<ByCountyWaffleToppings>
              columns={this.state.columnConfig}
              customSort={this.customSort}
              data={this.state.sortedRecords}
              testId="by-county"
            />
          )}
        </div>

      </>
    );
  }

  private buildRecords = (visibleRecords: ByCountyRecord[]) => {
    /* oxlint-disable sort-keys */
    return visibleRecords.map((rd) => ({
      [wafflesKeyColumnName]: rd.key,
      state: rd.stateAbbreviation,
      county: rd.county,
      network: rd.network ? <Link key={rd.network} to={`${this.props.providersEndpointFunc(SummaryResultNetworkType.Network, rd.resultType, rd.key)}`}>{(rd.network || 0).toLocaleString()}</Link> : <span key="0">0</span>,
      prospect: rd.prospect ? <Link key={rd.prospect} to={`${this.props.providersEndpointFunc(SummaryResultNetworkType.Prospect, rd.resultType, rd.key)}`}>{(rd.prospect || 0).toLocaleString()}</Link> : <span key="0">0</span>,
    }));
    /* oxlint-enable sort-keys */
  }

  private getColumnConfig = () => {
    return {
      county: {
        displayText: "County",
        testId: "county",
      },
      network: {
        displayText: "Network",
        testId: "network",
      },
      prospect: {
        displayText: "Prospect",
        hidden: !this.props.records.some((r) => !!r.prospect),
        testId: "prospect",
      },
      state: {
        displayText: "State",
        testId: "state",
      },
    };
  }

  private visibleRecords = (stateAbbreviation: string) => {
    return this.props.records.filter((r) => r.stateAbbreviation === stateAbbreviation);
  }

  private handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const abbreviation = e.target.value;
    const visibleRecords = this.visibleRecords(abbreviation);

    this.setState({
      selectedState: abbreviation,
      sortedRecords: this.buildRecords(visibleRecords),
    });
  }

  private customSort = (records: ByCountyWaffleToppings[], column: keyof ByCountyWaffleToppings, direction: SortDirections) => {
    const data = [...records];
    if (direction === SortDirections.None) {
      return data;
    }
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (column) {
      case "county":
      case "state":
        return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      case "network":
      case "prospect":
        return data.sort(
          (a, b) => {
            return (parseInt(a[column].key.toString(), 10) > parseInt(b[column].key.toString(), 10) ? 1 : -1) * multiplier;
          });
    }
  }
}

export { ByCounty };
